import type { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { SelectHeader } from 'atoms/SelectHeader';

type Props<T extends Rarity> = {
  rarities:
    | T[]
    | {
        value: T;
        badgeContent?: ReactNode;
      }[];
  selectedRarity: T;
  onChange: (rarity: T) => void;
} & Pick<
  ComponentProps<typeof FilterInDropdown>,
  'buttonColor' | 'fullWidth' | 'buttonSize' | 'blur'
>;

export const SelectRarity = <T extends Rarity>({
  rarities: raritiesProps,
  selectedRarity,
  onChange,
  buttonSize = 'small',
  fullWidth = false,
  buttonColor = 'tertiary',
  blur = false,
}: Props<T>) => {
  const rarities = raritiesProps.map(r =>
    typeof r === 'object' ? r : { value: r, badgeContent: undefined }
  );
  const iconSize = (() => {
    switch (buttonSize) {
      case 'large':
        return 3;
      case 'medium':
        return 2.5;
      default:
      case 'small':
        return 2;
    }
  })();
  const options = rarities.map(({ value: r, badgeContent }) => ({
    label: (
      <Horizontal as="span" gap={2}>
        <ScarcityIcon rarity={r} size={iconSize} />
        <Badge badgeContent={badgeContent} size="small">
          <FormattedMessage {...scarcityMessages[r]} />
        </Badge>
      </Horizontal>
    ),
    value: r,
  }));

  return (
    <FilterInDropdown
      buttonLabel={<ScarcityIcon rarity={selectedRarity} size={iconSize} />}
      buttonSize={buttonSize}
      buttonColor={buttonColor}
      fullWidth={fullWidth}
      blur={blur}
    >
      {({ closeDropdown }) => (
        <>
          <SelectHeader>
            <FormattedMessage
              id="SelectRarity.title"
              defaultMessage="Select rarity"
            />
          </SelectHeader>
          <RadioGroup
            modalSelect
            options={options}
            value={selectedRarity}
            name="rarity-selector"
            onChange={rarity => {
              onChange(rarity);
              closeDropdown();
            }}
          />
        </>
      )}
    </FilterInDropdown>
  );
};
